import { FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { createReview } from "../api/createReview";
import Button from "../components/buttons/Button";
import PageWrapper from "../components/common/PageWrapper";
import StarIndicator from "../components/common/StarIndicator";
import CheckboxField from "../components/fields/CheckboxField";
import TextAreaField from "../components/fields/TextAreaField";
import { Title } from "../components/Titles";
import { Spacer } from "../components/utils/Spacer";
import { showToast } from "../utils/showToast";

const NewReviewContainer = styled.div`
	height: 100vh;
	display: flex;
	flex-flow: column;
	min-height: 0;
`;

const FormWrapper = styled.form`
	display: flex;
	flex-flow: column;
	height: 100%;
`;

const PageSubtitle = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: #2f334b;
	text-align: left;
	margin-left: 23px;
`;

const InsidePageWrapper = styled.div`
	margin-left: 23px;
`;

const CommentWrapper = styled.div`
	height: 100%;
	padding: 23px;
`;

const ButtonWrapper = styled.div`
	padding: 23px;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: center;
`;

const ErrorMessage = styled.div`
	display: flex;
	margin-left: 23px;
	height: 15px;
	line-height: 0.6rem;
	color: #ed182a;
	font-size: 0.7rem;
	flex-shrink: 0;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
`;

interface INewReviewForm {
	rating: number;
	comment: string;
	anonymous: boolean;
}

/**
 * ### Tela de nova avaliação do profissional
 * Permite ao usuário dar uma opiniao sobre o profissional
 */
const ProfessionalNewReviewsScreen: FC = () => {
	const { scheduleId } = useParams<{ codProfissional: string; scheduleId: string }>();
	const navigate = useNavigate();
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		setError,
		clearErrors,
		reset,
		formState: { errors },
	} = useForm<INewReviewForm>({
		defaultValues: {
			rating: 0,
			comment: "",
			anonymous: false,
		},
	});

	const handleChangeRating = useCallback((stars: number) => {
		setValue("rating", stars);
		clearErrors("rating");
	}, []);

	const handleCheckboxChange = useCallback((value: boolean) => {
		setValue("anonymous", value);
	}, []);

	const onSubmit = handleSubmit(async (data: INewReviewForm) => {
		if (data.rating === 0) {
			setError("rating", {
				message: "Informe uma nota de 1 a 5",
				type: "required",
			});
			return;
		}

		const review = {
			note: data.rating,
			description: data.comment,
			anonimo: data.anonymous,
		};

		if (scheduleId) {
			const response = await createReview(review, scheduleId);
			if (response) {
				reset();
				showToast("success", {
					text: "Agradecemos sua avaliação! Ela ficará visível no app em breve.",
					timer: 6000,
					timerProgressBar: true,
				});
				navigate("/consultas", { replace: true });
			}
		}
	});

	return (
		<PageWrapper>
			<NewReviewContainer>
				<Title>Avaliar consulta</Title>
				<Spacer size={21} />
				<FormWrapper onSubmit={onSubmit}>
					<PageSubtitle>Atribua uma nota de 1 a 5 para seu atendimento:</PageSubtitle>
					<InsidePageWrapper>
						<StarIndicator grade={watch("rating")} maxGrade={5} callbackFn={handleChangeRating} />
					</InsidePageWrapper>
					<ErrorMessage>{errors.rating ? errors.rating.message : ""}</ErrorMessage>
					<PageSubtitle>Deixe um comentário:</PageSubtitle>

					<CommentWrapper>
						<TextAreaField maxLength={500} {...register("comment")} />
					</CommentWrapper>

					<InsidePageWrapper>
						<CheckboxField checked={watch("anonymous")} handleCheckboxChange={handleCheckboxChange} label="Não me identificar na avaliação" />
					</InsidePageWrapper>

					<ButtonWrapper>
						<Button type="submit">Enviar avaliação</Button>
					</ButtonWrapper>
				</FormWrapper>
			</NewReviewContainer>
		</PageWrapper>
	);
};

export default ProfessionalNewReviewsScreen;
